import { CALLABLE_STATUSES } from '@delvero/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import { signOut, useSession } from '../lib/auth';
import { orpc } from '../lib/orpc';
import { useShell } from '../shell';
import { Icon, type IconName } from './Icon';

const NAV: { to: string; label: string; icon: IconName }[] = [
  { to: '/', label: 'Dashboard', icon: 'dashboard' },
  { to: '/research', label: 'Research', icon: 'search' },
  { to: '/pipeline', label: 'Pipeline', icon: 'pipeline' },
  { to: '/calling', label: 'Dzwonienie', icon: 'phone' },
  { to: '/costs', label: 'Koszty', icon: 'coins' },
];

const callable = new Set<string>(CALLABLE_STATUSES);

function initials(name: string | undefined): string {
  if (!name) return 'TY';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || 'TY';
}

export function Sidebar() {
  const { activeAppId } = useShell();
  const { data: session } = useSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const leadsQuery = useQuery({
    ...orpc.leads.list.queryOptions({ input: { appId: activeAppId ?? '' } }),
    enabled: !!activeAppId,
  });
  const researchQuery = useQuery({
    ...orpc.research.list.queryOptions({ input: { appId: activeAppId ?? '' } }),
    enabled: !!activeAppId,
  });

  const leads = leadsQuery.data ?? [];
  const research = researchQuery.data ?? [];
  const queueCount = leads.filter((l) => callable.has(l.status)).length;

  return (
    <aside className="sidebar">
      <div className="logo-row">
        <div className="logo-mark">»</div>
        <div>
          <div className="logo-name">Delvero</div>
          <div className="logo-sub">Sales OS</div>
        </div>
      </div>
      <nav className="nav">
        {NAV.map((n) => {
          const active = n.to === '/' ? pathname === '/' : pathname.startsWith(n.to);
          return (
            <Link key={n.to} to={n.to} className={`nav-item${active ? ' active' : ''}`}>
              <Icon name={n.icon} size={15} />
              <span>{n.label}</span>
              {n.to === '/calling' && queueCount > 0 ? <span className="nav-count">{queueCount}</span> : null}
              {n.to === '/pipeline' ? <span className="nav-count muted">{leads.length}</span> : null}
              {n.to === '/research' && research.length > 0 ? (
                <span className="nav-count muted">{research.length}</span>
              ) : null}
            </Link>
          );
        })}
      </nav>
      <div className="sidebar-foot">
        <div className="me-row">
          <span className="me-avatar">{initials(session?.user?.name)}</span>
          <span className="me-name">{session?.user?.name ?? 'Solo founder'}</span>
          <span className="me-actions">
            <button className="icon-btn" title="Wyloguj" onClick={() => void signOut()}>
              <Icon name="logout" size={14} />
            </button>
          </span>
        </div>
      </div>
    </aside>
  );
}
